import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useFilters, TimePeriod } from '../../../contexts/FiltersContext';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Snackbar,
  Alert,
  Popover,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import CarbonCareDateRangePicker, {
  COMPLEMENTARY_RANGE_PRESETS,
} from '../../shared/CarbonCareDatePicker';
import ExportJobsWidget from '../ExportJobsWidget/ExportJobsWidget';
import { useAnalyticsData } from '../../../contexts/AnalyticsDataContext';
import HeaderFilters from './HeaderFilters';
import { useSplitView } from '../../../contexts/SplitViewContext';
import { dashboardApi } from '../../../services/dashboardApi';
import analyticsService from '../../../services/analyticsApi';
import { useAuthStore } from '../../../store/authStore';
import { companyApi } from '../../../services/api/companyApi';


const timeFilterOptions: Array<{ key: TimePeriod; value: string; label: string }> = [
  { key: 'YTD', value: 'YTD', label: 'YTD' },
  { key: 'MTD', value: 'MTD', label: 'MTD' },
  { key: 'L12M', value: 'L12M', label: 'L12M' },
  { key: 'HISTORIC', value: 'Last Month', label: 'Last Month' },
  { key: 'RANGE', value: 'Range', label: 'Range' },
];

interface TopPanelProps {
  pageType?: 'analytics' | 'emissions';
  showSplitViewControls?: boolean;
  printAreaId?: string;
}

// Pure: compute the from/to date range for a non-RANGE time period.
// RANGE clears both dates (handled by caller); here we only map fixed periods.
export const computePeriodDates = (
  period: TimePeriod,
  today: Date
): { fromDate: Date | null; toDate: Date | null } => {
  switch (period) {
    case 'YTD':
      return { fromDate: new Date(today.getFullYear(), 0, 1), toDate: today };
    case 'MTD':
      return { fromDate: new Date(today.getFullYear(), today.getMonth(), 1), toDate: today };
    case 'L12M':
      return {
        fromDate: new Date(today.getFullYear(), today.getMonth() - 12, today.getDate()),
        toDate: today,
      };
    case 'HISTORIC':
      return {
        fromDate: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        toDate: new Date(today.getFullYear(), today.getMonth(), 0),
      };
    default:
      return { fromDate: null, toDate: null };
  }
};

// Pure: parse a YYYY-MM-DD context date string into a local midnight Date, or null.
export const parseInitialDate = (dateStr?: string): Date | null =>
  dateStr ? new Date(dateStr + 'T00:00:00') : null;

type PdfBlockBound = { top: number; bottom: number; el: HTMLElement };

// Pure: should a straddling block be pushed to the next page rather than cut?
// A block is pushed unless it wastes too much page space — except charts/maps,
// which we always prefer to push intact.
const shouldPushBlock = (block: PdfBlockBound, idealEnd: number, maxWaste: number): boolean => {
  const wastedSpace = idealEnd - block.top;
  if (wastedSpace <= maxWaste) return true;
  const isChart =
    block.el.matches('canvas, .recharts-wrapper, .leaflet-container') ||
    block.el.querySelector('canvas, .recharts-wrapper, .leaflet-container');
  return Boolean(isChart);
};

// Pure: find the y position to break a PDF page at, preferring not to split a
// content block across pages. Returns idealEnd when no better break is found.
export const findPageBreakPoint = (
  yStart: number,
  idealEnd: number,
  pageH: number,
  blockBounds: PdfBlockBound[]
): number => {
  // If we are wasting too much space (more than 40% of the page),
  // we might prefer to just cut through the block rather than pushing it.
  const maxWaste = pageH * 0.4;

  for (const block of blockBounds) {
    const straddles = block.top < idealEnd && block.bottom > idealEnd && block.top > yStart;
    const fitsNextPage = block.bottom - block.top <= pageH;
    if (straddles && fitsNextPage && shouldPushBlock(block, idealEnd, maxWaste)) {
      return block.top;
    }
  }
  return idealEnd;
};

// Pure: given the print area id, return the id of the sibling split panel to
// hide during capture (or null when not in split view).
export const getSiblingPanelId = (printAreaId?: string): string | null => {
  if (printAreaId === 'split-view-left') return 'split-view-right';
  if (printAreaId === 'split-view-right') return 'split-view-left';
  return null;
};

type FilterDataItem = { value: string };
type FilterSummarySource = {
  licenseeData: FilterDataItem[];
  subCompanyData: FilterDataItem[];
  branchData: FilterDataItem[];
  subCustomerData: FilterDataItem[];
  transportModeData: FilterDataItem[];
  countryOriginData: FilterDataItem[];
  countryDestinationData: FilterDataItem[];
};

// Pure: build the human-readable filter summary lines for the PDF header.
export const buildFilterParts = (ctx: FilterSummarySource): string[] => {
  const specs: Array<[string, FilterDataItem[]]> = [
    ['Licensee', ctx.licenseeData],
    ['Sub Company', ctx.subCompanyData],
    ['Branch', ctx.branchData],
    ['Sub Customer', ctx.subCustomerData],
    ['Transport Mode', ctx.transportModeData],
    ['Origin Country', ctx.countryOriginData],
    ['Destination Country', ctx.countryDestinationData],
  ];
  const parts: string[] = [];
  for (const [label, data] of specs) {
    if (data.length > 0) {
      parts.push(`${label}: ${data.map(f => f.value).join(', ')}`);
    }
  }
  return parts;
};

type PeriodLabelSource = {
  timeFilter?: { key?: string };
  fromToDate?: { fromDate: string; toDate: string };
};

// Pure: compute the period label string shown in the PDF header.
export const computePeriodLabel = (
  ctx: PeriodLabelSource,
  options: Array<{ key: string; label: string }>
): string => {
  if (ctx.timeFilter?.key === 'RANGE' && ctx.fromToDate) {
    return `Range: ${ctx.fromToDate.fromDate} to ${ctx.fromToDate.toDate}`;
  }
  const found = options.find(o => o.key === ctx.timeFilter?.key);
  return found ? found.label : (ctx.timeFilter?.key || 'All');
};

// Pure: logo dimensions preserving aspect ratio, clamped to a max width.
export const computeLogoDimensions = (
  naturalWidth: number,
  naturalHeight: number,
  maxH: number,
  maxW: number
): { logoW: number; logoH: number } => {
  const aspectRatio = naturalWidth / naturalHeight;
  let logoW = maxH * aspectRatio;
  let logoH = maxH;
  if (logoW > maxW) {
    logoW = maxW;
    logoH = maxW / aspectRatio;
  }
  return { logoW, logoH };
};

type ComputedLike = {
  overflowX: string;
  backdropFilter: string;
  backgroundColor: string;
  paddingTop: string;
  paddingBottom: string;
  marginTop: string;
  marginBottom: string;
};

// Pure: given the computed style + classList of a descendant, return the list
// of CSS property mutations to apply for PDF capture. Each entry is applied
// with style.setProperty(prop, value, 'important') by the caller.
export const computeDescendantStyleOps = (
  computed: ComputedLike,
  isScrollableXClass: boolean
): Array<[string, string]> => {
  const ops: Array<[string, string]> = [
    ['overflow-y', 'visible'],
    ['max-height', 'none'],
    ['animation', 'none'],
  ];

  const isScrollableX =
    computed.overflowX === 'auto' ||
    computed.overflowX === 'scroll' ||
    isScrollableXClass;
  if (isScrollableX) {
    ops.push(['overflow-x', 'hidden']);
  }

  if (computed.backdropFilter && computed.backdropFilter !== 'none') {
    ops.push(['backdrop-filter', 'none'], ['-webkit-backdrop-filter', 'none']);
  }

  const bg = computed.backgroundColor;
  if (bg && bg.startsWith('rgba') && !bg.includes(', 0)')) {
    ops.push(['background', '#ffffff']);
  }

  const pt = Number.parseInt(computed.paddingTop);
  const pb = Number.parseInt(computed.paddingBottom);
  if (pt > 24) ops.push(['padding-top', `${pt / 2}px`]);
  if (pb > 24) ops.push(['padding-bottom', `${pb / 2}px`]);
  const mt = Number.parseInt(computed.marginTop);
  const mb = Number.parseInt(computed.marginBottom);
  if (mt > 20) ops.push(['margin-top', `${mt / 2}px`]);
  if (mb > 20) ops.push(['margin-bottom', `${mb / 2}px`]);

  return ops;
};

// Selectors for major content blocks that should not be split across PDF pages.
const PDF_BLOCK_SELECTORS = [
  '.MuiCard-root',
  '.MuiPaper-root',
  '.MuiTableContainer-root',
  'table',
  'tr',                     // table rows
  '.MuiTableRow-root',      // MUI table rows
  'canvas',                 // chart canvases
  '.recharts-wrapper',
  '.leaflet-container',     // maps
  '[class*="chart"]',
  '[class*="Chart"]',
  '[class*="map"]',
  '[class*="Map"]',
].join(',');

// Collect vertical block boundaries (in canvas pixels) for smart page breaks,
// sorted by top. Side-effecting on the DOM only via reads.
const collectBlockBounds = (
  element: HTMLElement,
  canvas: { height: number }
): PdfBlockBound[] => {
  const elementRect = element.getBoundingClientRect();
  const scaleY = canvas.height / element.scrollHeight;
  const blockBounds: PdfBlockBound[] = [];
  element.querySelectorAll(PDF_BLOCK_SELECTORS).forEach((block) => {
    const el = block as HTMLElement;
    const rect = el.getBoundingClientRect();
    const top = (rect.top - elementRect.top + element.scrollTop) * scaleY;
    const bottom = (rect.bottom - elementRect.top + element.scrollTop) * scaleY;
    if (bottom - top > 20 * scaleY) {
      blockBounds.push({ top, bottom, el });
    }
  });
  blockBounds.sort((a, b) => a.top - b.top);
  return blockBounds;
};

type PdfPageLayout = {
  margin: number;
  topPad: number;
  usableWidth: number;
  imgWidth: number;
  imgHeight: number;
  ratio: number;
  srcPageHeightP1: number;
  srcPageHeightRest: number;
};

// Slice the captured canvas into PDF pages, honoring smart page breaks.
// Preserves the original add-page / drawImage / addImage call sequence.
const renderPdfPages = (
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  blockBounds: PdfBlockBound[],
  layout: PdfPageLayout
): void => {
  const { margin, topPad, usableWidth, imgWidth, imgHeight, ratio, srcPageHeightP1, srcPageHeightRest } = layout;
  let yOffset = 0;
  let pageNum = 0;
  while (yOffset < imgHeight) {
    if (pageNum > 0) {
      pdf.addPage();
    }
    const isFirstPage = pageNum === 0;
    const srcPageH = isFirstPage ? srcPageHeightP1 : srcPageHeightRest;
    const yImageStart = isFirstPage ? margin + topPad : margin;

    const idealEnd = yOffset + srcPageH;
    const breakAt = idealEnd >= imgHeight ? imgHeight : findPageBreakPoint(yOffset, idealEnd, srcPageH, blockBounds);
    const sliceHeight = Math.min(breakAt - yOffset, imgHeight - yOffset);
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = imgWidth;
    pageCanvas.height = sliceHeight;
    const ctx = pageCanvas.getContext('2d')!;
    ctx.drawImage(canvas, 0, yOffset, imgWidth, sliceHeight, 0, 0, imgWidth, sliceHeight);
    const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.75);
    const sliceScaledHeight = sliceHeight * ratio;
    pdf.addImage(pageImgData, 'JPEG', margin, yImageStart, usableWidth, sliceScaledHeight);
    yOffset += sliceHeight;
    pageNum++;
  }
};

// Pure: header bar height in mm, growing with the number of wrapped filter rows.
export const computeHeaderHeight = (filterCount: number): number =>
  18 + (filterCount > 0 ? Math.ceil(filterCount / 3) * 6 : 0);

type PdfHeaderInfo = {
  margin: number;
  usableWidth: number;
  pdfWidth: number;
  headerH: number;
  reportTitle: string;
  reportDate: string;
  licenseeName: string;
  periodLabel: string;
  filterParts: string[];
  logoImg: HTMLImageElement | null;
};

// Draw the green header bar (title, logo, generated date, licensee name, period,
// and wrapped filter chips) on the first PDF page. Side-effecting on `pdf`.
const drawPdfHeader = (pdf: jsPDF, info: PdfHeaderInfo): void => {
  const { margin, usableWidth, pdfWidth, headerH, reportTitle, reportDate, licenseeName, periodLabel, filterParts, logoImg } = info;

  // Green header background
  pdf.setFillColor(232, 245, 233); // #e8f5e9
  pdf.rect(margin, margin, usableWidth, headerH, 'F');
  // Green bottom border line
  pdf.setDrawColor(76, 175, 80); // #4caf50
  pdf.setLineWidth(0.5);
  pdf.line(margin, margin + headerH, margin + usableWidth, margin + headerH);

  // Report title
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(27, 94, 32); // #1b5e20
  pdf.text(reportTitle, margin + 4, margin + 7);

  // -- Licensee logo (top-right, full header height) --
  const logoAreaRight = pdfWidth - margin - 4;
  const logoPadding = 2; // padding inside header for logo
  const logoMaxH = headerH - logoPadding * 2; // full header height minus padding
  const logoMaxW = 30; // max logo width in mm

  if (logoImg) {
    // Calculate logo dimensions preserving aspect ratio, using full header height
    const { logoW, logoH } = computeLogoDimensions(
      logoImg.naturalWidth,
      logoImg.naturalHeight,
      logoMaxH,
      logoMaxW
    );

    // Center logo vertically in header, right-aligned
    const logoX = logoAreaRight - logoW;
    const logoY = margin + (headerH - logoH) / 2;

    // Convert image to canvas for jsPDF
    const logoCanvas = document.createElement('canvas');
    logoCanvas.width = logoImg.naturalWidth;
    logoCanvas.height = logoImg.naturalHeight;
    const logoCtx = logoCanvas.getContext('2d')!;
    logoCtx.drawImage(logoImg, 0, 0);
    const logoDataUrl = logoCanvas.toDataURL('image/png');
    pdf.addImage(logoDataUrl, 'PNG', logoX, logoY, logoW, logoH);
  }

  // Generated date (right-aligned, left of logo if present)
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(120, 120, 120);
  const generatedDateX = logoImg ? (logoAreaRight - logoMaxW - 4) : logoAreaRight;
  pdf.text(`Generated: ${reportDate}`, generatedDateX, margin + 7, { align: 'right' });

  // Licensee name right below generated date
  if (licenseeName) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(27, 94, 32); // #1b5e20
    pdf.text(licenseeName, generatedDateX, margin + 12, { align: 'right' });
  }

  // Period pill
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(56, 142, 60); // #388e3c
  pdf.text(`Period: ${periodLabel}`, margin + 4, margin + 13);

  // Filter chips (wrap 3 per row)
  if (filterParts.length > 0) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(50, 50, 50);
    const chunkSize = 3;
    for (let i = 0; i < filterParts.length; i += chunkSize) {
      const row = filterParts.slice(i, i + chunkSize).join('   |   ');
      pdf.text(row, margin + 4, margin + 17 + Math.floor(i / chunkSize) * 6);
    }
  }
};

const TopPanel: React.FC<TopPanelProps> = ({ pageType, showSplitViewControls = true, printAreaId }) => {
  const theme = useTheme();
  const useFilterContext = useFilters();
  const { isSplitView, setIsSplitView } = useSplitView();
  const { activeTabId } = useAnalyticsData();
  const { user } = useAuthStore();

  const [success, setSuccess] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [exportWidgetAnchor, setExportWidgetAnchor] = useState<HTMLElement | null>(null);
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [company, setCompany] = useState<any>(null);

  // Fetch company data for licensee logo in PDF
  useEffect(() => {
    const fetchCompanyData = async () => {
      const token = localStorage.getItem('authToken');
      if (user?.companyId && token) {
        try {
          const response = await companyApi.getById(user.companyId);
          setCompany(response);
        } catch (error) {
          console.error('Failed to fetch company data:', error);
        }
      }
    };
    fetchCompanyData();
  }, [user?.companyId]);

  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>(
    (useFilterContext.timeFilter?.key) || 'L12M'
  );
  const [fromDate, setFromDate] = useState<Date | null>(
    parseInitialDate(useFilterContext.fromToDate?.fromDate)
  );
  const [toDate, setToDate] = useState<Date | null>(
    parseInitialDate(useFilterContext.fromToDate?.toDate)
  );

  // Refs + state for the sliding-pill indicator behind the active time-filter button
  const periodGroupRef = useRef<HTMLDivElement | null>(null);
  const periodButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [pillStyle, setPillStyle] = useState<{ left: number; width: number; ready: boolean }>({
    left: 0,
    width: 0,
    ready: false,
  });

  useLayoutEffect(() => {
    const updatePill = () => {
      const activeBtn = periodButtonRefs.current[selectedPeriod];
      if (!activeBtn) return;
      setPillStyle({
        left: activeBtn.offsetLeft,
        width: activeBtn.offsetWidth,
        ready: true,
      });
    };
    updatePill();
    globalThis.addEventListener('resize', updatePill);
    return () => globalThis.removeEventListener('resize', updatePill);
  }, [selectedPeriod, isSplitView]);

  // Get today's date
  const today = new Date();

  const updateDatesForPeriod = (period: TimePeriod) => {
    if (period === 'RANGE') {
      setFromDate(null);
      setToDate(null);
      return;
    }
    const { fromDate: calculatedFromDate, toDate: calculatedToDate } = computePeriodDates(period, today);
    console.log('period in calucation',calculatedFromDate, calculatedToDate)
    setFromDate(calculatedFromDate);
    setToDate(calculatedToDate);
  };

  React.useEffect(() => {
    if (useFilterContext.timeFilter?.key && useFilterContext.timeFilter.key !== selectedPeriod) {
      setSelectedPeriod(useFilterContext.timeFilter.key);
      updateDatesForPeriod(useFilterContext.timeFilter.key);
    }
  }, [useFilterContext.timeFilter?.key]);

  const handlePeriodSelect = (period: TimePeriod) => {
    console.log("period in select",period);
    setSelectedPeriod(period);
    
    // For RANGE mode, don't trigger time filter change yet
    // Wait for user to select dates and click Apply
    if (period === 'RANGE') {
      // For RANGE mode, just clear the dates and context
      updateDatesForPeriod('RANGE');
      useFilterContext.setFromToDate(undefined);
    } else {
      console.log('useFilterContext',useFilterContext);
      useFilterContext.changeTimeFilter({ key: period, value: period });
      updateDatesForPeriod(period);
      useFilterContext.setFromToDate(undefined);
    }
  };

  const handleDateChange = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (fromDate && toDate) {
      // Format dates to YYYY-MM-DD for API
      const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Set the date range and trigger the time filter change
      useFilterContext.setFromToDate({
        fromDate: formatDate(fromDate),
        toDate: formatDate(toDate),
      });
      useFilterContext.changeTimeFilter({ key: 'RANGE', value: 'Range' });
    }
  };

  const handleExportIconClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportWidgetAnchor(event.currentTarget);
  };

  const handleCloseExportWidget = () => {
    setExportWidgetAnchor(null);
  };

  const createPdfOverlay = (): HTMLDivElement => {
    const overlay = document.createElement('div');
    overlay.id = 'pdf-generating-overlay';
    overlay.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
      background: rgba(255,255,255,0.85); display: flex; flex-direction: column;
      align-items: center; justify-content: center; z-index: 9999;
      backdrop-filter: blur(2px);
    `;
    overlay.innerHTML = `
      <div style="display:flex;flex-direction:column;align-items:center;gap:16px;">
        <div class="pdf-spinner" style="width:48px;height:48px;border:4px solid #e0e0e0;border-top:4px solid #1976d2;border-radius:50%;animation:pdf-spin 1s linear infinite;"></div>
        <span style="font-family:Roboto,sans-serif;font-size:16px;font-weight:500;color:#333;">Generating PDF...</span>
        <span style="font-family:Roboto,sans-serif;font-size:13px;color:#888;">Please wait, this may take a few seconds</span>
      </div>
      <style>@keyframes pdf-spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}</style>
    `;
    document.body.appendChild(overlay);
    return overlay;
  };

  const removePdfOverlay = () => {
    const overlay = document.getElementById('pdf-generating-overlay');
    if (overlay) overlay.remove();
  };

  const handleDownloadPDF = async () => {
    const element = document.getElementById(printAreaId || 'page-print-area');
    if (!element) {
      setError('Could not find dashboard content to export.');
      return;
    }

    setIsPdfGenerating(true);
    createPdfOverlay();

    // Yield to the browser so the overlay renders before heavy work begins
    await new Promise(resolve => setTimeout(resolve, 50));

    // Save original inline styles so we can restore after capture
    const saved: Array<{ el: HTMLElement; props: Record<string, string> }> = [];

    const save = (el: HTMLElement, props: string[]) => {
      const orig: Record<string, string> = {};
      props.forEach(p => { orig[p] = el.style.getPropertyValue(p); });
      saved.push({ el, props: orig });
    };

    const restoreAll = () => {
      saved.forEach(({ el, props }) => {
        for (const [prop, val] of Object.entries(props)) {
          if (val) {
            el.style.setProperty(prop, val);
          } else {
            el.style.removeProperty(prop);
          }
        }
      });
    };

    try {
      // 0. Hide TopPanel elements inside the print area (they appear in split view)
      const topPanelsInPrintArea = element.querySelectorAll('.top-panel-no-pdf');
      topPanelsInPrintArea.forEach((tp) => {
        const el = tp as HTMLElement;
        save(el, ['display']);
        el.style.setProperty('display', 'none', 'important');
      });

      // 1. Hide the sibling split panel (if any) to prevent bleed-through during capture
      const siblingId = getSiblingPanelId(printAreaId);
      const siblingEl = siblingId ? document.getElementById(siblingId) : null;
      if (siblingEl) {
        save(siblingEl, ['display']);
        siblingEl.style.setProperty('display', 'none', 'important');
      }

      // 2. Expand all ancestors up to body (these have height:100vh / overflow:hidden)
      let parent = element.parentElement;
      while (parent && parent !== document.body) {
        save(parent, ['overflow', 'height', 'max-height']);
        parent.style.setProperty('overflow', 'visible', 'important');
        parent.style.setProperty('max-height', 'none', 'important');
        parent.style.setProperty('height', 'auto', 'important');
        parent = parent.parentElement;
      }

      // 3. Expand the capture element itself and force solid, fully-opaque styles
      save(element, ['overflow', 'max-height', 'animation', 'opacity', 'background-color']);
      element.style.setProperty('overflow', 'visible', 'important');
      element.style.setProperty('max-height', 'none', 'important');
      element.style.setProperty('animation', 'none', 'important');
      element.style.setProperty('opacity', '1', 'important');
      element.style.setProperty('background-color', '#ffffff', 'important');

      // 4. Expand descendants only vertically where needed,
      //    and replace semi-transparent backgrounds with solid white
      element.querySelectorAll('*').forEach((desc) => {
        const el = desc as HTMLElement;
        if (el.classList.contains('top-panel-no-pdf')) return; // already hidden
        const computed = globalThis.getComputedStyle(el);

        // Save original states
        save(el, ['overflow', 'overflow-x', 'overflow-y', 'max-height', 'backdrop-filter', '-webkit-backdrop-filter', 'background', 'animation']);

        // Compute and apply all style mutations for PDF capture (height expansion,
        // horizontal-overflow clamp, backdrop-filter removal, opaque backgrounds,
        // and compressed paddings/margins).
        const ops = computeDescendantStyleOps(computed, el.classList.contains('MuiTabs-scroller'));
        ops.forEach(([prop, val]) => el.style.setProperty(prop, val, 'important'));
      });

      // Wait for browser reflow
      await new Promise(resolve => setTimeout(resolve, 400));

      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: -globalThis.scrollY,
        width: element.scrollWidth,
        height: element.scrollHeight,
      });

      // 5. Collect vertical positions of major content blocks for smart page breaks.
      //    We look for Cards, Tables, Charts, and Maps — the components most likely
      //    to be large and should not be split across pages.
      const blockBounds = collectBlockBounds(element, canvas);

      restoreAll();

      // -- Build filter summary strings for PDF header --
      const periodLabel = computePeriodLabel(useFilterContext, timeFilterOptions);
      const filterParts = buildFilterParts(useFilterContext);

      const reportTitle = pageType === 'analytics' ? 'Analytics Report' : 'Emissions Report';
      const reportDate = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

      // Pre-load licensee logo image if available
      const licenseeLogoUrl = company?.logoUrl || company?.logo_url;
      const licenseeName = company?.name || '';
      let logoImg: HTMLImageElement | null = null;
      if (licenseeLogoUrl) {
        try {
          logoImg = await new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('Failed to load licensee logo'));
            img.src = licenseeLogoUrl;
          });
        } catch (logoErr) {
          console.warn('Could not load licensee logo for PDF:', logoErr);
        }
      }

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      // Landscape A4
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const margin = 5;
      const usableWidth = pdfWidth - margin * 2;

      // -- Draw header bar on first page using native jsPDF --
      const headerH = computeHeaderHeight(filterParts.length);
      drawPdfHeader(pdf, {
        margin,
        usableWidth,
        pdfWidth,
        headerH,
        reportTitle,
        reportDate,
        licenseeName,
        periodLabel,
        filterParts,
        logoImg,
      });

      // Now lay out dashboard canvas images, leaving room for header on page 1
      const topPad = headerH + 2; // extra space below header on page 1
      const usableHeightP1 = pdfHeight - margin * 2 - topPad;
      const usableHeightRest = pdfHeight - margin * 2;
      const ratio = usableWidth / imgWidth;
      renderPdfPages(pdf, canvas, blockBounds, {
        margin,
        topPad,
        usableWidth,
        imgWidth,
        imgHeight,
        ratio,
        srcPageHeightP1: usableHeightP1 / ratio,
        srcPageHeightRest: usableHeightRest / ratio,
      });

      const date = new Date().toISOString().slice(0, 10);
      const pageName = pageType === 'analytics' ? 'analytics' : 'emissions-dashboard';
      pdf.save(`${pageName}-${date}.pdf`);
      setSuccess('PDF downloaded successfully!');
    } catch (err) {
      console.error('PDF generation failed:', err);
      restoreAll();
      setError('Failed to generate PDF. Please try again.');
    } finally {
      removePdfOverlay();
      setIsPdfGenerating(false);
    }
  };

  const handleSetDefaultFilter = async () => {
    if (!activeTabId || selectedPeriod === 'RANGE') return;
    try {
      setIsSettingDefault(true);
      if (pageType === 'emissions') {
        const payload = {
          page_key: 'emission_dashboard',
          analytics_tab_id: activeTabId,
          time_period: selectedPeriod,
        };
        await dashboardApi.setDefaultFilter(payload);
      } else {
        const payload = {
          page_key: 'analytics',
          analytics_tab_id: activeTabId,
          time_period: selectedPeriod,
        };
        await analyticsService.postData(analyticsService.endpoints.analyticsDefaultFilter, payload);
      }
      setSuccess('Default filter saved successfully!');
    } catch (err) {
      console.error('Failed to set default filter:', err);
      setError('Failed to save default filter.');
    } finally {
      setIsSettingDefault(false);
    }
  };

  // Derived JSX visibility flags (kept out of the markup to limit nesting).
  const isRange = selectedPeriod === 'RANGE';
  const showSplitToggle = showSplitViewControls && (pageType === 'analytics' || pageType === 'emissions');
  const showExportAndPdf = showSplitViewControls || isSplitView;

  return (
    <Box className="top-panel-no-pdf" sx={{ '@media print': { display: 'none' } }}>
      {/* Filters and Actions Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexWrap: 'nowrap',
          gap: isSplitView ? 1 : 2
        }}
      >
        {/* Time Filter Selection */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: isSplitView ? 'wrap' : 'nowrap',
            flex: 1,
            gap: 1
          }}
        >
          {/* Hierarchical Filters for Split View */}
          {isSplitView && (
            <Box sx={{ mr: 1 }}>
              <HeaderFilters />
            </Box>
          )}

          <Box
            ref={periodGroupRef}
            role="group"
            sx={{
              position: 'relative',
              display: 'inline-flex',
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
              overflow: 'hidden',
              backgroundColor: 'background.paper',
            }}
          >
            {/* Sliding background pill */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: pillStyle.width,
                transform: `translateX(${pillStyle.left}px)`,
                backgroundColor: 'primary.main',
                transition: pillStyle.ready
                  ? 'transform 280ms cubic-bezier(0.4, 0, 0.2, 1), width 280ms cubic-bezier(0.4, 0, 0.2, 1)'
                  : 'none',
                zIndex: 0,
                pointerEvents: 'none',
              }}
            />
            {timeFilterOptions.map((option) => {
              const active = selectedPeriod === option.key;
              return (
                <Button
                  key={option.key}
                  ref={(el) => { periodButtonRefs.current[option.key] = el; }}
                  onClick={() => handlePeriodSelect(option.key)}
                  disableRipple
                  size="small"
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    minWidth: isSplitView ? 60 : 70,
                    px: isSplitView ? 1 : 2,
                    border: 'none',
                    borderRadius: 0,
                    color: active ? 'primary.contrastText' : 'primary.main',
                    backgroundColor: 'transparent',
                    transition: 'color 280ms cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover, &:focus, &:focus-visible, &.Mui-focusVisible': {
                      backgroundColor: active ? 'transparent' : (theme: any) => alpha(theme.palette.primary.main, 0.08),
                      color: active ? 'primary.contrastText' : 'primary.main',
                    },
                  }}
                >
                  {option.label}
                </Button>
              );
            })}
          </Box>


          {/* Date Range Picker - Always visible, enabled only for RANGE */}
          <Box
            component="form"
            onSubmit={handleDateChange}
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              ml: isSplitView ? 0 : 1
            }}
          >
            <CarbonCareDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              onFromDateChange={setFromDate}
              onToDateChange={setToDate}
              maxDate={today}
              disabled={selectedPeriod !== 'RANGE'}
              // YTD / MTD / L12M / Last Month are already buttons on this bar.
              presets={COMPLEMENTARY_RANGE_PRESETS}
            />
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={selectedPeriod !== 'RANGE' || !fromDate || !toDate}
            >
              Apply
            </Button>
          </Box>
        </Box>

        {/* Export Actions */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Set as Default Button - Hidden in split view */}
          {!isSplitView && (
            <Tooltip
              title={isRange ? 'Cannot set as default for custom range' : 'Save current tab and time period as default'}
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSetDefaultFilter}
                  disabled={isSettingDefault || isRange}
                  sx={{
                    whiteSpace: 'nowrap',
                    color: isRange ? 'text.disabled' : '#90BF45',
                    borderColor: isRange ? 'divider' : '#90BF45',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#90BF45',
                      background: alpha('#90BF45', 0.04),
                    },
                    '&.Mui-disabled': {
                      borderColor: 'divider',
                      color: 'text.disabled',
                    },
                  }}
                >
                  {isSettingDefault ? 'Saving...' : 'Set as Default'}
                </Button>
              </span>
            </Tooltip>
          )}

          {showSplitToggle && (
            <Tooltip
              title={isSplitView ? 'Switch to Single View - Please keep left side view' : 'Split View'}
              arrow
              componentsProps={{ tooltip: { sx: { maxWidth: 'none', whiteSpace: 'nowrap' } } }}
            >
              <IconButton
                onClick={() => setIsSplitView(!isSplitView)}
                color={isSplitView ? 'secondary' : 'primary'}
                sx={{
                  bgcolor: isSplitView ? alpha(theme.palette.secondary.main, 0.1) : 'transparent',
                  '&:hover': {
                    bgcolor: isSplitView ? alpha(theme.palette.secondary.main, 0.2) : alpha(theme.palette.primary.main, 0.1),
                  }
                }}
              >
                <ViewWeekIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Export and Print - Always visible in both views in split mode */}
          {showExportAndPdf && (
            <>
              <Tooltip title="Export">
                <IconButton onClick={handleExportIconClick} color="primary">
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={isPdfGenerating ? 'Generating PDF...' : 'Download PDF'}>
                <span>
                  <IconButton
                    onClick={handleDownloadPDF}
                    color="primary"
                    disabled={isPdfGenerating}
                  >
                    {isPdfGenerating ? <CircularProgress size={24} /> : <PictureAsPdfIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={2000}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>

      {/* Export Jobs Widget Popover */}
      <Popover
        open={Boolean(exportWidgetAnchor)}
        anchorEl={exportWidgetAnchor}
        onClose={handleCloseExportWidget}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          mt: 1,
        }}
      >
        <ExportJobsWidget
          onSuccess={(msg) => {
            setSuccess(msg);
          }}
          onError={(msg) => setError(msg)}
        />
      </Popover>
    </Box>
  );
};

export default TopPanel;
